import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, FileSpreadsheet, ShieldCheck } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { ObangsaekStripe } from '@/components/editorial';
import CaseStudyCard from '@/components/financial/CaseStudyCard';
import { CASE_STUDIES, getFeaturedCaseStudies } from '@/content/financial/case-studies';
import { CALCULATORS } from '@/content/financial/calculators';
import { PLAYBOOKS } from '@/content/financial/playbooks';
import { SECTION_INTRO } from '@/content/financial/editorial';

const BASE_URL = 'https://koreanhub.net';

/**
 * The Financial hub.
 *
 * Carries the section intro that explains why wealth-building analysis sits on
 * a Korean diaspora site. That intro is load-bearing rather than decorative:
 * most of the content below it is US micro-business material arriving next to
 * F-4 and remittance guides, and a reader who cannot tell why deserves an
 * answer above the fold.
 */
export default function Financial() {
  const featured = getFeaturedCaseStudies();

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'KoreanHub Financial',
    description: SECTION_INTRO.lede,
    url: `${BASE_URL}/financial`,
    hasPart: CASE_STUDIES.map((c) => ({
      '@type': 'Article',
      headline: c.title,
      url: `${BASE_URL}/financial/case-studies/${c.slug}`,
      datePublished: c.publishedAt,
    })),
  };

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="Financial — Small-Business Teardowns & Calculators"
        description="Teardowns of real micro-businesses with published numbers, interactive financial calculators, and execution playbooks — including the cross-border cases that only affect the Korean diaspora."
        path="/financial"
        jsonLd={collectionSchema}
      />

      {/* ── Header ── */}
      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <p className="text-[11px] font-bold uppercase tracking-wide text-cheong-500 dark:text-cheong-dark">
            {SECTION_INTRO.eyebrow}
          </p>
          <h1 className="mt-3 text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            {SECTION_INTRO.title}
          </h1>
          <p lang="ko" className="mt-1 font-korean text-lg text-ink-500 dark:text-ink-400">
            {SECTION_INTRO.titleKo}
          </p>
          <p className="mt-4 max-w-2xl text-base leading-7 text-ink-700 dark:text-ink-200">
            {SECTION_INTRO.lede}
          </p>
        </div>
      </header>

      <ObangsaekStripe />

      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {/* ── Why this section exists ── */}
        <section className="border-b border-ink-200 py-10 dark:border-surface-dark-border">
          <div className="max-w-2xl space-y-4">
            {SECTION_INTRO.body.map((para) => (
              <p key={para} className="text-base leading-7 text-ink-700 dark:text-ink-200">
                {para}
              </p>
            ))}
          </div>
          <Link
            to="/financial/methodology"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-cheong-500 hover:underline dark:text-cheong-dark"
          >
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            How we source every number
          </Link>
        </section>

        {/* ── Case studies ── */}
        <SectionHeading
          title="Case studies"
          titleKo="사례 연구"
          blurb={`${CASE_STUDIES.length} teardowns with the full economics, the execution sequence, and the failure modes.`}
          to="/financial/case-studies"
          linkLabel="All case studies"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((study) => (
            <CaseStudyCard key={study.slug} study={study} />
          ))}
        </div>

        {/* ── Calculators ── */}
        <SectionHeading
          title="Calculators"
          titleKo="계산기"
          blurb="Underwrite a deal, price a business, or find out what a side project actually pays per hour."
          to="/financial/calculators"
          linkLabel="All calculators"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {CALCULATORS.map((calc) => (
            <Link
              key={calc.slug}
              to={`/financial/calculators/${calc.slug}`}
              className="group flex items-start gap-4 rounded-xl border border-ink-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
            >
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cheong-500/10 text-cheong-500 dark:text-cheong-dark">
                <Calculator className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-base font-bold text-ink-900 dark:text-ink-50">
                  {calc.name}
                </span>
                <span
                  lang="ko"
                  className="mt-0.5 block font-korean text-xs text-ink-400"
                  aria-hidden="true"
                >
                  {calc.nameKo}
                </span>
                <span className="mt-2 block text-sm leading-6 text-ink-500 dark:text-ink-400">
                  {calc.summary}
                </span>
              </span>
            </Link>
          ))}
        </div>

        {/* ── Playbooks ── */}
        <SectionHeading
          title="Playbooks"
          titleKo="실행 가이드"
          blurb={`${PLAYBOOKS.length} execution blueprints that operationalise the case studies. Free — the documents are still in production.`}
          to="/financial/playbooks"
          linkLabel="All playbooks"
        />
        <div className="grid grid-cols-1 gap-5 pb-4 sm:grid-cols-3">
          {PLAYBOOKS.slice(0, 3).map((pb) => (
            <Link
              key={pb.slug}
              to="/financial/playbooks"
              className="group rounded-xl border border-ink-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
            >
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-hwang-600 dark:text-hwang-500">
                <FileSpreadsheet className="h-3 w-3" aria-hidden="true" />
                {pb.format}
              </span>
              <span className="mt-2 block text-base font-bold leading-snug text-ink-900 dark:text-ink-50">
                {pb.title}
              </span>
              <span className="mt-2 block text-sm leading-6 text-ink-500 line-clamp-3 dark:text-ink-400">
                {pb.summary}
              </span>
            </Link>
          ))}
        </div>

        {/* ── Fine print ── */}
        <section className="my-12 rounded-xl border border-ink-200 bg-ink-50 p-5 dark:border-surface-dark-border dark:bg-white/[0.03]">
          <p className="text-sm leading-relaxed text-ink-700 dark:text-ink-200">
            Everything in this section is educational. Nothing here is investment, tax, legal, or
            accounting advice, and the calculators produce estimates from the figures you enter —
            not underwriting.{' '}
            <Link
              to="/financial/disclosures"
              className="font-medium text-cheong-500 underline dark:text-cheong-dark"
            >
              Read the full disclosures
            </Link>
            .
          </p>
        </section>
      </div>
    </div>
  );
}

function SectionHeading({
  title,
  titleKo,
  blurb,
  to,
  linkLabel,
}: {
  title: string;
  titleKo: string;
  blurb: string;
  to: string;
  linkLabel: string;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 pb-5 pt-10">
      <div>
        <h2 className="text-2xl font-bold text-ink-900 dark:text-ink-50">
          {title}{' '}
          <span lang="ko" className="font-korean text-base font-medium text-ink-400">
            {titleKo}
          </span>
        </h2>
        <p className="mt-1.5 max-w-2xl text-sm leading-6 text-ink-500 dark:text-ink-400">{blurb}</p>
      </div>
      <Link
        to={to}
        className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-cheong-500 hover:gap-2 dark:text-cheong-dark [transition:gap_150ms_ease-out]"
      >
        {linkLabel}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}
